<template>
  <div class="users">
    <popup
      :title="$t('view.users.popup.deleteGroup.title')"
      ref="groupDelDialog"
    >
      <p>
        {{ $t("view.users.popup.deleteGroup.text1") }}
        <b>{{ groupToDelete ? groupToDelete.name : "" }}</b>
        {{ $t("view.users.popup.deleteGroup.text2") }}
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.groupDelDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="trash" @click="deleteGroup(groupToDelete)" color="red">{{
          $t("btn.delete")
        }}</btn>
      </template>
    </popup>

    <popup :title="$t('view.users.popup.deleteUser.title')" ref="userDelDialog">
      <div class="users__details-picture">
        <img
          v-show="selectedUser.profile_pic"
          :src="selectedUser.profile_pic"
          ref="image"
          alt="User's profile picture"
        />
        <div
          class="users__details-picture-placeholder"
          v-if="!selectedUser.profile_pic"
        >
          <fai icon="eye-slash"></fai>
        </div>
      </div>
      <p style="text-align: center">
        {{ $t("view.users.popup.deleteUser.text1") }}
        <b style="display: block">{{
          selectedUser
            ? `${selectedUser.first_name} ${selectedUser.last_name}`
            : ""
        }}</b>
        {{ $t("view.users.popup.deleteUser.text2") }}
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.userDelDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="trash" @click="deleteUser(selectedUser)" color="red">{{
          $t("btn.delete")
        }}</btn>
      </template>
    </popup>

    <popup
      :title="$t('view.users.popup.assignUser.title')"
      ref="userAssignDialog"
    >
      <div class="users__details-picture">
        <img
          v-show="selectedUser.profile_pic"
          :src="selectedUser.profile_pic"
          ref="image"
          alt="User's profile picture"
        />
        <div
          class="users__details-picture-placeholder"
          v-if="!selectedUser.profile_pic"
        >
          <fai icon="eye-slash"></fai>
        </div>
      </div>
      <p style="text-align: center">
        {{ $t("view.users.popup.assignUser.text1") }}
        <b style="display: block">{{
          selectedUser
            ? `${selectedUser.first_name} ${selectedUser.last_name}`
            : ""
        }}</b>
        {{ $t("view.users.popup.assignUser.text2") }}
      </p>

      <multiselect
        class="users__multiselect"
        open-direction="top"
        trackBy="id"
        label="name"
        placeholder
        deselectLabel
        selectLabel
        v-model="selectedGroups"
        id="userGroups"
        :hideSelected="true"
        :max-height="240"
        :close-on-select="true"
        :multiple="true"
        :options="availableGroups"
      ></multiselect>

      <template v-slot:buttons>
        <btn icon="times" @click="$refs.userAssignDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="check" @click="assignUser(selectedUser)" color="green">{{
          $t("btn.assign")
        }}</btn>
      </template>
    </popup>

    <popup :title="$t('view.users.popup.export.title')" ref="exportDialog">
      <p style="text-align: left">
        {{ $t("view.users.popup.export.text1") }}
        <b>{{
          selectedGroup
            ? selectedGroup.name
            : $t("view.users.popup.export.allUsers")
        }}</b>
        {{ $t("view.users.popup.export.text2") }}
      </p>
      <div class="users__export-popup-fields">
        <h4>{{ $t("view.users.popup.export.file") }}</h4>
        <min-sel
          class="users__export-format-select"
          v-model="exportFileFormat"
          :label="$t('view.users.popup.export.fileFormat')"
          framed
        >
          <option value="csv">CSV</option>
          <option value="json">JSON</option>
        </min-sel>
        <h4>{{ $t("view.users.popup.export.usersFields") }}</h4>
        <div class="users__export-fields-wrapper">
          <sw-button
            class="users__export-field-switch"
            v-for="(val, field) in userExportFields"
            :key="field"
            :label="$t(`view.users.popup.export.fieldsUsers.${field}`)"
            v-model="userExportFields[field]"
          ></sw-button>
        </div>
        <h4>{{ $t("view.users.popup.export.dataFields") }}</h4>
        <div class="users__export-fields-wrapper">
          <sw-button
            class="users__export-field-switch"
            v-for="(val, field) in dataExportFields"
            :key="field"
            :label="field"
            v-model="dataExportFields[field]"
          ></sw-button>
        </div>
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.exportDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="file-download" @click="exportData" color="green">{{
          $t("btn.export")
        }}</btn>
      </template>
    </popup>

    <popup
      :title="$t('view.users.popup.details.title')"
      ref="userDetailsDialog"
    >
      <div class="users__details-wrapper">
        <div>
          <div class="users__details-picture">
            <img
              v-show="selectedUser.profile_pic"
              :src="selectedUser.profile_pic"
              ref="image"
              alt="User's profile picture"
            />
            <div
              class="users__details-picture-placeholder"
              v-if="!selectedUser.profile_pic"
            >
              <fai icon="eye-slash"></fai>
            </div>
          </div>
          <div class="users__details-wrapper">
            <div class="users__details-column">
              <in
                disabled
                v-model="selectedUser.first_name"
                :label="$t('view.users.popup.details.firstName')"
              ></in>
              <in
                disabled
                v-model="selectedUser.gender"
                :label="$t('view.users.popup.details.gender')"
              ></in>
              <in
                disabled
                v-model="selectedUser.outside_id"
                :label="$t('view.users.popup.details.outsideId')"
              ></in>
              <in
                disabled
                v-model="selectedUser.created_at"
                :label="$t('view.users.popup.details.createdAt')"
              ></in>
              <in
                disabled
                v-model="selectedUser.privacy_accepted"
                :label="$t('view.users.popup.details.privacyAccepted')"
              ></in>
              <in
                disabled
                v-model="selectedUser.communicator_id"
                :label="$t('view.users.popup.details.communicatorId')"
              ></in>
            </div>
            <div class="users__details-column">
              <in
                disabled
                v-model="selectedUser.last_name"
                :label="$t('view.users.popup.details.lastName')"
              ></in>
              <in
                disabled
                v-model="selectedUser.locale"
                :label="$t('view.users.popup.details.locale')"
              ></in>
              <in
                disabled
                v-model="selectedUser.id"
                :label="$t('view.users.popup.details.id')"
              ></in>
              <in
                disabled
                v-model="selectedUser.last_data_update"
                :label="$t('view.users.popup.details.lastDataUpdate')"
              ></in>
              <in
                disabled
                v-model="selectedUser.moderator_chat"
                :label="$t('view.users.popup.details.moderatorChat')"
              ></in>
              <in
                disabled
                v-model="selectedUser.source_id"
                :label="$t('view.users.popup.details.sourceId')"
              ></in>
            </div>
          </div>
        </div>
        <div class="users__details-variables">
          <div class="users__details-variables-wrapper">
            <h4 class="no-top-margin">Database variables</h4>
            <div class="users__details-variables-json">
              <pre>{{ JSON.stringify(selectedUser.dbVariables, null, 2) }}</pre>
            </div>
          </div>
          <div class="users__details-variables-wrapper">
            <h4>Redis variables</h4>
            <div class="users__details-variables-json">
              <pre>{{
                JSON.stringify(selectedUser.redisVariables, null, 2)
              }}</pre>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.userDetailsDialog.close()">{{
          $t("btn.close")
        }}</btn>
      </template>
    </popup>

    <popup
      :title="$t('view.users.popup.variables.title')"
      ref="variablesManageDialog"
    >
      <div class="users__variables-wrapper">
        <div
          class="users__variables-row"
          v-for="(val, field) in dataExportFields"
          :key="field"
        >
          <div class="users__variables-name">{{ field }}</div>
          <div class="users__variables-actions">
            <div
              class="users__variables-action"
              @click="openVariableDeleteDialog(field)"
            >
              <fai class="users__variables-action-icon" icon="trash"></fai>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.variablesManageDialog.close()">{{
          $t("btn.close")
        }}</btn>
      </template>
    </popup>

    <popup
      :title="$t('view.users.popup.deleteVar.title')"
      ref="varDeleteDialog"
    >
      <p style="max-width: 700px">
        {{ $t("view.users.popup.deleteVar.text1") }}
        <b>{{ varFieldToDelete ? varFieldToDelete : "" }}.</b>
        {{ $t("view.users.popup.deleteVar.text2") }}
        <in
          class="users__var-del-dialog-input"
          v-model="varToDeletePass"
          :placeholder="varFieldToDelete"
        ></in>
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.varDeleteDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn
          icon="trash"
          :inactive="varFieldToDelete !== varToDeletePass"
          @click="deleteVariable"
          color="red"
          >{{ $t("btn.delete") }}</btn
        >
      </template>
    </popup>

    <div class="users__sidebar">
      <div class="users__sidebar-container">
        <h3 class="users__sidebar-title">{{ $t("view.users.groups") }}</h3>
        <div class="users__filter-card">
          <short-card
            @selected="
              setSelected(null);
              filters.group = null;
              reloadUsers();
            "
            :isSelected="isSelected(null)"
          >
            <div>{{ $t("view.users.noGroup") }}</div>
          </short-card>
        </div>
        <div class="users__filter-card">
          <short-card
            @selected="
              setSelected('last24h');
              filters.group = 'last24h';
              reloadUsers();
            "
            :isSelected="isSelected('last24h')"
          >
            <div>{{ $t("view.users.last24h") }}</div>
          </short-card>
        </div>
        <div
          class="users__item"
          v-for="(item, index) in $store.state.usersGroups"
          :key="item.id"
        >
          <short-card
            v-model="$store.state.usersGroups[index]"
            @selected="
              setSelected(item);
              filters.group = selectedGroup;
              reloadUsers();
            "
            :isSelected="isSelected(item)"
          >
            <div
              @dblclick="$set(editModes, index, true)"
              style="user-select: none"
            >
              {{ item.name }}
            </div>
            <div v-show="editModes[index]" class="users__group-edit">
              <in
                class="users__group-edit-input"
                :placeholder="$t('view.users.renamePlaceholder')"
                @keyup="renameInputKeyup($event, item, index)"
              ></in>
              <div class="users__group-edit-icon">
                <fai
                  @click="renameGroup($event, item, index)"
                  icon="save"
                ></fai>
              </div>
              <div
                @click="$set(editModes, index, false)"
                class="users__group-edit-icon"
              >
                <fai icon="times"></fai>
              </div>
            </div>
            <div class="users__group-delete-wrapper">
              <div
                class="users__group-delete"
                @click="
                  groupToDelete = item;
                  $refs.groupDelDialog.open();
                "
              >
                {{ $t("btn.delete") }}
              </div>
            </div>
          </short-card>
        </div>
        <div
          class="users__new-group"
          :class="{ 'users__new-group--active': addMode }"
        >
          <in class="users__input" v-model="newGroup.name" maxlength="32"></in>
          <btn class="users__button" @click="createGroup" icon="plus" small>{{
            $t("btn.create")
          }}</btn>
        </div>
        <span class="users__sidebar-create" @click="addMode = !addMode">{{
          addMode ? $t("btn.cancel") : $t("view.users.newGroup")
        }}</span>
      </div>
    </div>
    <div class="users__editor editor">
      <div class="users__top-buttons">
        <btn icon="user-tag" @click="$refs.variablesManageDialog.open()">{{
          $t("view.users.manageVariables")
        }}</btn>
        <btn icon="file-export" @click="$refs.exportDialog.open()">{{
          $t("view.users.exportData")
        }}</btn>
        <!-- <btn
          icon="user-friends"
          color="green"
          @click="$refs.exportDialog.open()"
          >{{ $t("view.users.usersAssign") }}</btn
        >
        <btn icon="user-times" color="red" @click="$refs.exportDialog.open()">{{
          $t("view.users.usersDelete")
        }}</btn>-->
      </div>
      <tab
        class="users__table"
        :fields="fields"
        :data="users"
        :amount="amount"
        :page="page"
        :sortedBy="sortBy"
        :sortMode="sortMode"
        @pageChange="changePage"
        @search="search"
        @userDetails="displayDetails"
        @userDelete="deleteUserPopup"
        @userAssign="assignUserPopup"
        @sort="sort"
      ></tab>
    </div>
  </div>
</template>

<script>
import csv from "json2csv";
import axios from "../axios";
import URL from "url";
import Multiselect from "../files/vue-multiselect.min.js";

export default {
  components: { Multiselect },
  data() {
    return {
      selectedGroup: null,
      groupToDelete: null,
      addMode: false,
      newGroup: {
        name: "New group",
      },
      filters: {
        group: null,
      },
      sortBy: null,
      sortMode: "asc",
      users: [],
      amount: 0,
      page: 1,
      lastUrl: null,
      selectedUser: {},
      selectedGroups: [],
      availableGroups: [],
      fields: {
        first_name: {
          label: this.$t("view.users.table.firstName"),
          type: "string",
          sortable: true,
          align: "left",
        },
        last_name: {
          label: this.$t("view.users.table.lastName"),
          type: "string",
          sortable: true,
          align: "left",
        },
        locale: {
          label: this.$t("view.users.table.locale"),
          type: "locale",
          align: "center",
        },
        gender: {
          label: this.$t("view.users.table.gender"),
          type: "gender",
          align: "center",
        },
        communicator_id: {
          label: this.$t("view.users.table.communicator"),
          type: "communicator",
          align: "center",
        },
        source_id: {
          label: this.$t("view.users.table.source"),
          type: "source",
          align: "center",
        },
        operations: {
          label: this.$t("view.users.table.operations"),
          type: "buttons",
          align: "center",
          list: [
            {
              name: this.$t("view.users.table.details"),
              eventName: "userDetails",
              icon: "info",
            },
            {
              name: this.$t("view.users.table.group"),
              eventName: "userAssign",
              icon: "user-friends",
              color: "green",
            },

            {
              name: this.$t("view.users.table.delete"),
              eventName: "userDelete",
              icon: "trash-alt",
              color: "red",
            },
          ],
        },
      },
      exportFileFormat: "csv",
      userExportFields: {
        first_name: true,
        last_name: true,
        locale: true,
        gender: true,
        outside_id: true,
      },
      dataExportFields: {},
      varFieldToDelete: null,
      varToDeletePass: "",
      editModes: [],
    };
  },

  methods: {
    isSelected(item) {
      if (item == this.selectedGroup) return true;
      else return false;
    },

    setSelected(filter) {
      this.selectedGroup = filter;
    },

    async createGroup() {
      try {
        const groupReq = await axios.post("/api/users/group", {
          name: this.newGroup.name,
        });
        this.$store.commit("PUSH_NEW_USERS_GROUP", groupReq.data.created);
        this.newGroup.name = "New group";
        this.addMode = false;
        this.editModes.push(false);
        this.notify("success", "view.users.success.groupCreate");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.users.error.groupCreate");
      }
    },

    async deleteGroup(group) {
      try {
        await axios.delete("/api/users/group", {
          data: {
            id: group.id,
          },
        });
        this.$store.commit("DELETE_USERS_GROUP", group.id);
        this.groupToDelete = null;
        this.notify("success", "view.users.success.groupDelete");
        this.$refs.groupDelDialog.close();
        this.editModes.pop();
      } catch (e) {
        console.error(e);
        this.notify("error", "view.users.error.groupDelete");
        this.$refs.groupDelDialog.close();
      }
    },

    async assignUserPopup(user) {
      this.selectedUser = user;
      this.availableGroups = this.$store.state.usersGroups;
      const tmpGroups = await axios.post("/api/users/user-get-groups", {
        id: user.id,
      });
      this.selectedGroups = tmpGroups.data.filteredGroups;
      this.$forceUpdate();
      this.$refs.userAssignDialog.open();
    },

    async assignUser(user) {
      try {
        const mappedGroups = this.selectedGroups.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        await axios.post("/api/users/user-set-groups", {
          id: user.id,
          groups: mappedGroups,
        });
        this.selectedUser = {};
        this.forceChangePage();
        this.notify("success", "view.users.success.userAssign");
        this.$refs.userAssignDialog.close();
      } catch (e) {
        console.error(e);
        this.notify("error", "view.users.error.userAssign");
        this.$refs.userAssignDialog.close();
      }
    },

    deleteUserPopup(user) {
      this.selectedUser = user;
      this.$forceUpdate();
      this.$refs.userDelDialog.open();
    },

    async deleteUser(user) {
      try {
        await axios.delete("/api/users/user-delete", {
          data: {
            id: user.id,
          },
        });
        this.$store.commit("DELETE_USER", user.id);
        this.selectedUser = {};
        this.notify("success", "view.users.success.userDelete");
        this.$refs.userDelDialog.close();
        this.forceChangePage();
      } catch (e) {
        console.error(e);
        this.notify("error", "view.users.error.userDelete");
        this.$refs.userDelDialog.close();
      }
    },

    createUsersListUrl() {
      const paramsMap = new Map();
      paramsMap.set("page", this.page);
      if (this.filters.group) {
        if (typeof this.filters.group === "object")
          paramsMap.set("group", this.filters.group.id);
        else paramsMap.set("group", this.filters.group);
      }
      if (this.filters.search) paramsMap.set("search", this.filters.search);
      if (this.sortBy) paramsMap.set("sort", this.sortBy);
      if (this.sortMode) paramsMap.set("sortmode", this.sortMode);
      const params = new URLSearchParams(paramsMap);
      return `/api/users/list?${params.toString()}`;
    },

    reloadUsers() {
      this.page = 1;
      this.changePage(this.page);
    },

    search(str) {
      if (str === this.filters.search) return;
      if (!str.length) {
        this.filters.search = null;
        this.reloadUsers();
        return;
      }
      this.filters.search = str;
      this.page = 1;
      this.changePage(this.page);
    },

    sort(by) {
      if (by === this.sortBy && this.sortMode === "desc") {
        this.sortBy = null;
        this.sortMode = null;
      } else if (by === this.sortBy && this.sortMode === "asc") {
        this.sortBy = by;
        this.sortMode = "desc";
      } else {
        this.sortBy = by;
        this.sortMode = "asc";
      }
      this.page = 1;
      this.changePage(this.page);
    },

    async changePage(page) {
      try {
        this.page = page;
        const url = this.createUsersListUrl();
        if (this.lastUrl === url) return;
        const usersReq = await axios.get(url);
        this.users = usersReq.data.users;
        this.amount = parseInt(usersReq.data.count);
        this.lastUrl = url;
      } catch (e) {
        this.notify("error", "view.users.error.load");
      }
    },

    async forceChangePage(page) {
      try {
        this.page = page;
        const url = this.createUsersListUrl();
        const usersReq = await axios.get(url);
        this.users = usersReq.data.users;
        this.amount = parseInt(usersReq.data.count);
        this.lastUrl = url;
      } catch (e) {
        this.notify("error", "view.users.error.load");
      }
    },

    async displayDetails(user) {
      try {
        this.selectedUser = JSON.parse(JSON.stringify(user));
        this.selectedUser.id = this.selectedUser.id.toString();
        this.selectedUser.communicator_id =
          this.selectedUser.communicator_id.toString();
        this.selectedUser.source_id = this.selectedUser.source_id.toString();
        this.selectedUser.privacy_accepted = this.selectedUser.privacy_accepted
          ? this.$t("common.yes")
          : this.$t("common.no");
        this.selectedUser.moderator_chat = this.selectedUser.moderator_chat
          ? this.$t("common.yes")
          : this.$t("common.no");

        await this.$refs.userDetailsDialog.open();
        this.selectedUser.dbVariables = "Loading...";
        this.selectedUser.redisVariables = "Loading...";

        const variablesReq = await axios.get(`/api/users/data?user=${user.id}`);
        this.selectedUser.dbVariables = variablesReq.data.dbVariables;
        this.selectedUser.redisVariables = variablesReq.data.redisVariables;
        if (this.$refs.image.naturalWidth === 0)
          this.selectedUser.profile_pic = null;
        this.$forceUpdate();
      } catch (e) {
        this.selectedUser.dbVariables = "Load error.";
        this.selectedUser.redisVariables = "Load error.";
        this.$forceUpdate();
        this.notify("error", "view.users.error.detailsLoad");
        console.error(e);
      }
    },

    formExportData(data) {
      const users = data.users;
      const fields = data.data;
      let results = [];

      for (let i = 0; i < fields.length; i++) {
        users[fields[i].user_id][fields[i].name] = fields[i].data.value;
      }

      const usersKeys = Object.keys(users);
      for (let i = 0; i < usersKeys.length; i++) {
        results.push(users[usersKeys[i]]);
      }

      return results;
    },

    async saveToFile(data, name, type) {
      try {
        const blob = new Blob([data], { type });
        const e = document.createEvent("MouseEvents");
        const a = document.createElement("a");

        a.download = name;
        a.href = window.URL.createObjectURL(blob);
        a.dataset.downloadurl = [type, a.download, a.href].join(":");
        e.initEvent(
          "click",
          true,
          false,
          window,
          0,
          0,
          0,
          0,
          0,
          false,
          false,
          false,
          false,
          0,
          null
        );
        a.dispatchEvent(e);
      } catch (e) {
        throw e;
      }
    },

    async exportData() {
      try {
        let userFields = [];
        let dataFields = [];

        for (let field in this.userExportFields) {
          if (this.userExportFields[field]) userFields.push(field);
        }

        for (let field in this.dataExportFields) {
          if (this.dataExportFields[field]) dataFields.push(field);
        }

        const dataReq = await axios.get(
          `/api/users/users-data?group=${
            this.selectedGroup ? this.selectedGroup.id : 0
          }&uFields=${userFields.toString()}&dFields=${dataFields.toString()}`
        );

        const json = this.formExportData(dataReq.data);
        if (this.exportFileFormat === "json") {
          await this.saveToFile(
            JSON.stringify(json, null, 2),
            "powerbot-export.json",
            "application/json"
          );
        } else if (this.exportFileFormat === "csv") {
          const fields = userFields.concat(dataFields);
          const csvParser = new csv.Parser({ fields });
          const resultCsv = csvParser.parse(json);
          await this.saveToFile(resultCsv, "powerbot-export.csv", "text/csv");
        }
        this.$refs.exportDialog.close();
        this.notify("success", "view.users.success.dataExport");
      } catch (e) {
        this.$refs.exportDialog.close();
        this.notify("error", "view.users.error.dataExport");
        console.error(e);
      }
    },

    openVariableDeleteDialog(field) {
      this.varFieldToDelete = field;
      this.$refs.varDeleteDialog.open();
    },

    async deleteVariable() {
      try {
        await axios.delete("/api/users/variable", {
          data: {
            variableName: this.varFieldToDelete,
          },
        });
        this.$delete(this.dataExportFields, this.varFieldToDelete);
        this.varFieldToDelete = null;
        this.$refs.varDeleteDialog.close();
        this.varToDeletePass = "";
        this.notify("success", "view.users.success.varDelete");
      } catch (e) {
        this.$refs.varDeleteDialog.close();
        this.notify("error", "view.users.error.varDelete");
        console.error(e);
      }
    },

    renameInputKeyup(e, b, c) {
      if (e.key === "Enter") this.renameGroup(e, b, c);
    },

    async renameGroup(e, group, i) {
      try {
        const el = e.path.find((v) =>
          v.classList.contains("users__group-edit")
        );
        const newName = el.querySelector("input").value;
        if (!newName || newName === group.name) {
          this.$set(this.editModes, i, false);
          el.querySelector("input").value = "";
        }

        const data = {
          id: group.id,
          name: newName,
        };

        await axios.put("/api/users/group-rename", data);
        this.$store.commit("RENAME_USERS_GROUP", data);
        this.$set(this.editModes, i, false);
        this.notify("success", "view.users.success.groupRename");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.users.error.groupRename");
      }
    },
  },
  async created() {
    try {
      const usersReq = await axios.get("/api/users/list");
      const dbFieldsReq = await axios.get("/api/users/db-fields");

      for (let i = 0; i < dbFieldsReq.data.fields.length; i++) {
        this.dataExportFields[dbFieldsReq.data.fields[i].name] = false;
      }

      for (let g of this.$store.state.usersGroups) {
        this.editModes.push(false);
      }

      this.users = usersReq.data.users;
      this.amount = parseInt(usersReq.data.count);
    } catch (e) {
      this.notify("error", "view.users.error.load");
    }
  },
};
</script>

<style src="../files/vue-multiselect.min.css"></style>

<style lang="scss">
.users {
  &__multiselect {
    margin-bottom: 5px;

    .multiselect__select {
      height: 31px;
      padding-right: 0;
      margin-right: -3px;
      display: none;
    }

    .multiselect__tags {
      @include inputStyle(5px);
      min-height: 31px;
      width: 100%;
      padding-bottom: 3px;
    }

    .multiselect__placeholder {
      display: none;
    }

    .multiselect__option--highlight {
      background: $primary;

      &:after {
        background: $primary;
      }
    }

    .multiselect__tag {
      margin: 1px 3px;
      background: $primary;
      &-icon {
        &:after {
          color: #ffffff;
        }
        &:hover {
          background: $primaryHover;
        }
      }
    }
  }
}
</style>
<style lang="scss" scoped>
.users {
  display: flex;
  width: 100%;

  &__editor {
    flex-grow: 1;
    padding-right: 10px;
    overflow: hidden;
  }

  &__sidebar {
    @include viewSidebar();
    padding-top: 15px;
    height: calc(100% - 15px);
  }

  &__sidebar-title {
    margin-top: 0;
    margin-left: 4%;
  }

  &__filter-card {
    width: 92%;
    margin: 10px auto 10px auto;
  }

  &__sidebar-create {
    @include clickableText();
  }

  &__item {
    width: 92%;
    margin: 10px auto 10px auto;

    &:hover {
      .users__group-delete {
        display: block;
      }
    }
  }

  &__new-group {
    width: 80%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    height: auto;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.2s ease;

    &--active {
      max-height: 150px;
    }
  }

  &__group-delete {
    @include clickableText();
    margin: 0;
    font-style: normal;
    display: none;
  }

  &__group-delete-wrapper {
    @include alignedFlex();
    position: absolute;
    height: 100%;
    top: 0;
    right: 15px;
  }

  &__details-picture {
    width: 550px;
    margin-bottom: 25px;

    & > img {
      display: block;
      margin: 0 auto;
      border-radius: 50%;
      width: 100px;
      height: 100px;
      object-fit: cover;
    }
  }

  &__details-picture-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100px;
    height: 100px;
    border-radius: 50px;
    margin: 0 auto;
    background-color: $bgLightGrey;
    color: $inactiveGrey;
    font-size: 1.2em;
  }

  &__details-wrapper {
    @include alignedFlex();
    align-items: flex-start;
  }

  &__details-column {
    width: 250px;
    margin-right: 25px;
    & > * {
      margin-bottom: 8px;
    }
  }

  &__details-variables-wrapper {
    width: 370px;
    height: 100%;
  }

  &__details-variables-json {
    background-color: $bgLightGrey;
    padding: 1px 12px;
    border-radius: 4px;
    height: 200px;
    overflow: auto;
  }

  &__top-buttons {
    @include alignedFlex(flex-end);
    padding: 15px 20px 0 20px;

    & > div:not(:last-of-type) {
      margin-right: 10px;
    }
  }

  &__table {
    max-height: 90%;
  }

  &__export-popup-fields {
    margin-bottom: 20px;

    & > h4 {
      margin-bottom: 7px;
    }
  }

  &__export-fields-wrapper {
    @include alignedFlex(flex-start);
    flex-wrap: wrap;
    max-width: 75vw;
  }

  &__export-field-switch {
    width: 150px;
    margin-top: 12px;
  }

  &__export-format-select {
    width: 60px;
    margin-top: 10px;
  }

  &__variables-wrapper {
    padding: 3px 8px;
    max-height: 40vh;
    overflow-y: auto;
  }

  &__variables-row {
    padding: 8px 6px;
    display: flex;
    justify-content: space-between;

    &:not(:last-of-type) {
      border-bottom: 1px solid $borderGrey;
    }
  }

  &__variables-action-icon {
    color: #a7a7a7;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #858585;
    }
  }

  &__var-del-dialog-input {
    max-width: 300px;
    margin: 20px auto 0 auto;
  }

  &__group-edit {
    display: flex;
    align-items: center;
  }

  &__group-edit-input {
    margin-right: 4px;
    width: 100%;
  }

  &__group-edit-icon {
    margin: 0 4px;
    color: #a7a7a7;
    font-size: 1.1em;
    padding-top: 4px;

    &:hover {
      color: #858585;
    }
  }
}

.no-top-margin {
  margin-top: 0;
}
</style>
